import { useEffect, useRef, useState } from 'react';

/** SSE phase identifiers emitted by the transcript analysis orchestrator. */
export type AnalysisPhase =
  | 'analysis.queued'
  | 'analysis.loading'
  | 'analysis.scatter'
  | 'analysis.gate'
  | 'analysis.merging'
  | 'analysis.synthesizing'
  | 'analysis.validating'
  | 'analysis.persisting'
  | 'analysis.completed'
  | 'analysis.failed'
  | 'analysis.cancelled';

const TERMINAL_PHASES = new Set<AnalysisPhase>([
  'analysis.completed',
  'analysis.failed',
  'analysis.cancelled',
]);

/** Scatter/merge counts surfaced to the progress component. */
export interface AnalysisCounts {
  completed: number;
  total: number;
}

/** Reactive state exposed by the stream hook. */
export interface TranscriptAnalysisStreamState {
  phase: AnalysisPhase | null;
  progress: number;
  message: string;
  counts: AnalysisCounts | null;
  severity: string | null;
  isTerminal: boolean;
  hasError: boolean;
}

interface SSEEventPayload {
  message?: string;
  counts?: AnalysisCounts;
  severity?: string;
}

interface RawSSEEvent {
  event_id?: number;
  event_name?: string;
  phase?: string;
  progress?: number;
  payload?: SSEEventPayload;
}

const INITIAL_STATE: TranscriptAnalysisStreamState = {
  phase: null,
  progress: 0,
  message: 'Starting analysis…',
  counts: null,
  severity: null,
  isTerminal: false,
  hasError: false,
};

const RETRY_DELAY_MS = 2_000;

async function sleep(ms: number, signal: AbortSignal): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    const id = setTimeout(resolve, ms);
    signal.addEventListener('abort', () => {
      clearTimeout(id);
      reject(new DOMException('Aborted', 'AbortError'));
    });
  });
}

/**
 * Reads an SSE response body line-by-line and yields parsed event objects.
 * Only handles `data:` lines; ignores comments and id/retry directives.
 */
async function* readSseLines(response: Response, signal: AbortSignal): AsyncGenerator<RawSSEEvent> {
  const reader = response.body?.getReader();
  if (!reader) {
    return;
  }
  const decoder = new TextDecoder();
  let buffer = '';
  try {
    while (!signal.aborted) {
      const { done, value } = await reader.read();
      if (done) {
        break;
      }
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';
      for (const line of lines) {
        if (line.startsWith('data:')) {
          const json = line.slice(5).trim();
          if (!json) {
            continue;
          }
          try {
            yield JSON.parse(json) as RawSSEEvent;
          } catch {
            // ignore malformed events
          }
        }
      }
    }
  } finally {
    reader.cancel();
  }
}

export interface UseTranscriptAnalysisStreamOptions {
  streamUrl: string | null;
  accessToken: string | null;
}

/**
 * Subscribe to a transcript analysis SSE stream.
 *
 * Opens a fetch-based SSE connection on mount, reconnects automatically on
 * disconnect, and closes on unmount. Sends `Last-Event-ID` on reconnect to
 * replay only missed events.
 */
export function useTranscriptAnalysisStream({
  streamUrl,
  accessToken,
}: UseTranscriptAnalysisStreamOptions): TranscriptAnalysisStreamState {
  const [state, setState] = useState<TranscriptAnalysisStreamState>(INITIAL_STATE);
  const lastEventIdRef = useRef(0);

  useEffect(() => {
    if (!streamUrl || !accessToken) {
      return;
    }

    const resolvedUrl: string = streamUrl;
    const resolvedToken: string = accessToken;
    let cancelled = false;
    const controller = new AbortController();

    async function runStream(): Promise<void> {
      while (!cancelled && !controller.signal.aborted) {
        const headers: Record<string, string> = {
          Authorization: `Bearer ${resolvedToken}`,
          Accept: 'text/event-stream',
        };
        if (lastEventIdRef.current > 0) {
          headers['Last-Event-ID'] = String(lastEventIdRef.current);
        }

        let reachedTerminal = false;
        try {
          const response = await fetch(resolvedUrl, {
            headers,
            signal: controller.signal,
          });
          if (!response.ok || !response.body) {
            if (response.status === 401 || response.status === 403) {
              setState((prev) => ({
                ...prev,
                phase: 'analysis.failed',
                isTerminal: true,
                hasError: true,
                message: 'Authentication error — please refresh and try again.',
              }));
              break;
            }
            await sleep(RETRY_DELAY_MS, controller.signal);
            continue;
          }

          for await (const event of readSseLines(response, controller.signal)) {
            if (cancelled || controller.signal.aborted) {
              break;
            }
            const id = event.event_id ?? 0;
            if (id > 0) {
              lastEventIdRef.current = Math.max(lastEventIdRef.current, id);
            }
            const phase = (event.phase ?? null) as AnalysisPhase | null;
            const progress = event.progress ?? 0;
            const payload = event.payload ?? {};
            const isTerminal = phase !== null && TERMINAL_PHASES.has(phase);

            setState({
              phase,
              progress,
              message: payload.message ?? '',
              counts: payload.counts ?? null,
              severity: payload.severity ?? null,
              isTerminal,
              hasError: phase === 'analysis.failed' || phase === 'analysis.cancelled',
            });

            if (isTerminal) {
              reachedTerminal = true;
              break;
            }
          }
        } catch {
          // network error — retry after delay
        }

        if (reachedTerminal || cancelled || controller.signal.aborted) {
          break;
        }
        await sleep(RETRY_DELAY_MS, controller.signal).catch(() => undefined);
      }
    }

    void runStream();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [streamUrl, accessToken]);

  return state;
}
